"use strict";
"use client";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OptionList = OptionList;
const jsx_runtime_1 = require("react/jsx-runtime");
const utilities_1 = require("@kalkulacka-one/design-system/utilities");
const js_1 = require("@mdi/js");
const class_variance_authority_1 = require("class-variance-authority");
const react_1 = require("react");
const icon_1 = require("./icon");
const ROVING_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End"]);
// Deliberately not `role="listbox"`: every row is a real link or button, so native semantics stay honest instead of
// borrowing a widget role that promises single-selection state this list does not have. What a listbox gives for
// free (one Tab stop, arrows walking the rows) is built by hand: the caller marks one row `tabIndex={0}` and the rest
// `-1`, and this component moves that slot and real focus together.
//
// Arrows stop at the ends rather than wrapping: a search field sits right above the list, and looping from the last
// row back to the first would be a shorter trip than Shift+Tab back to where the user was typing.
function OptionList({ children, className, activeIndex, onActiveIndexChange, "aria-label": ariaLabel, ref }) {
    const containerRef = (0, react_1.useRef)(null);
    const rows = (0, react_1.useCallback)(() => Array.from(containerRef.current?.querySelectorAll('[data-option-row]:not([data-disabled="true"])') ?? []), []);
    (0, react_1.useImperativeHandle)(ref, () => ({
        focusActive: () => rows()[activeIndex]?.focus(),
        scrollActiveIntoView: () => rows()[activeIndex]?.scrollIntoView({ block: "nearest" }),
    }), [rows, activeIndex]);
    const handleKeyDown = (0, react_1.useCallback)((event) => {
        if (!ROVING_KEYS.has(event.key))
            return;
        const list = rows();
        if (list.length === 0)
            return;
        const current = list.indexOf(document.activeElement);
        if (current === -1)
            return;
        let next = current;
        if (event.key === "ArrowDown")
            next = Math.min(current + 1, list.length - 1);
        else if (event.key === "ArrowUp")
            next = Math.max(current - 1, 0);
        else if (event.key === "Home")
            next = 0;
        else if (event.key === "End")
            next = list.length - 1;
        event.preventDefault();
        list[next]?.focus();
        onActiveIndexChange(next);
    }, [rows, onActiveIndexChange]);
    // A row focused any other way (a click, Shift+Tab back in from below) becomes the roving slot too.
    const handleFocus = (0, react_1.useCallback)(() => {
        const current = rows().indexOf(document.activeElement);
        if (current !== -1)
            onActiveIndexChange(current);
    }, [rows, onActiveIndexChange]);
    return ((0, jsx_runtime_1.jsx)("section", { ref: containerRef, className: className, onKeyDown: handleKeyDown, onFocus: handleFocus, "aria-label": ariaLabel, children: children }));
}
const OptionListRowVariants = (0, class_variance_authority_1.cva)([
    "ko:group ko:grid ko:grid-cols-[auto_minmax(0,1fr)_auto] ko:grid-rows-[auto_minmax(0,max-content)] ko:content-center ko:items-center ko:w-full ko:min-h-[3.25rem] ko:px-4 ko:py-3",
    "ko:rounded-[1.125rem]",
    "ko:text-left ko:no-underline ko:text-inherit",
], {
    variants: {
        interactive: {
            true: [
                "ko:cursor-pointer ko:border-[1.5px] ko:border-neutral/15 ko:bg-white ko:shadow-sm",
                "ko:hover:bg-neutral/8",
                "ko:focus-visible:outline-2 ko:focus-visible:outline-offset-2 ko:focus-visible:outline-primary",
            ],
            false: ["ko:cursor-default ko:border-[1.5px] ko:border-neutral/15 ko:bg-neutral-disabled/55"],
        },
        highlighted: {
            true: "ko:ring-3 ko:ring-primary/55 ko:ring-offset-2",
            false: "",
        },
    },
    defaultVariants: {
        interactive: true,
        highlighted: false,
    },
});
function OptionListBadge({ children, className }) {
    return (0, jsx_runtime_1.jsx)("span", { className: (0, utilities_1.twMerge)("ko:col-start-1 ko:row-span-2 ko:mr-3 ko:flex ko:items-center", className), children: children });
}
function OptionListDescription({ children, className }) {
    return (0, jsx_runtime_1.jsx)("span", { className: (0, utilities_1.twMerge)("ko:col-start-2 ko:row-start-2 ko:truncate ko:text-s ko:text-neutral/70", className), children: children });
}
// A disabled row stops being a control rather than staying one that rejects clicks: it drops its href, its click
// handler and any tabIndex a roving list passed it.
function OptionListRow({ children, href, onClick, disabled = false, highlighted = false, tabIndex, className, ...rest }) {
    const classes = (0, utilities_1.twMerge)(OptionListRowVariants({ interactive: !disabled, highlighted }), className);
    const content = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [children, disabled ? null : (0, jsx_runtime_1.jsx)(icon_1.Icon, { icon: js_1.mdiChevronRight, decorative: true, size: "small", className: "ko:col-start-3 ko:row-span-2 ko:ml-3 ko:text-neutral/60" })] }));
    if (disabled) {
        return ((0, jsx_runtime_1.jsx)("div", { className: classes, "aria-disabled": "true", "data-option-row": true, "data-disabled": "true", ...rest, children: content }));
    }
    if (href) {
        return ((0, jsx_runtime_1.jsx)("a", { className: classes, href: href, onClick: onClick, tabIndex: tabIndex, "data-option-row": true, ...rest, children: content }));
    }
    if (onClick) {
        return ((0, jsx_runtime_1.jsx)("button", { type: "button", className: classes, onClick: onClick, tabIndex: tabIndex, "data-option-row": true, ...rest, children: content }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { className: classes, tabIndex: tabIndex, "data-option-row": true, ...rest, children: content }));
}
function OptionListTitle({ children, className }) {
    return ((0, jsx_runtime_1.jsx)("span", { className: (0, utilities_1.twMerge)("ko:col-start-2 ko:row-start-1 ko:truncate ko:text-[1.0625rem] ko:font-semibold ko:text-neutral ko:group-data-[disabled=true]:text-neutral/60", className), children: children }));
}
OptionList.Row = OptionListRow;
OptionList.Badge = OptionListBadge;
OptionList.Title = OptionListTitle;
OptionList.Description = OptionListDescription;
